import React, { Fragment, useContext, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  BuildingOfficeIcon,
  PlusIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import OrgDropdown from "components/OrgPages/OrgDropdown";
import OrgPageSettings from "components/OrgPages/OrgPageSettings";
import { OrgContext } from "contexts/OrgContext";
import { Button } from "components/Graphs/Button";
import { AddOrgModal } from "components/Modals/AddOrgModel";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import { Link } from "react-router-dom";
import { Cog6ToothIcon } from "@heroicons/react/24/solid";
import Spinner from "components/Graphs/InlineSpinner";

const OrgSettingsSlideOver: React.FC = (props) => {
  const orgContext = useContext(OrgContext);
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const oneClickDeployContext = useContext(OneClickDeployContext);

  return (
    <>
      <div
        data-testid="org-settings"
        className="hidden sm:-my-px sm:ml-2 sm:flex sm:space-x-8 h-full"
      >
        <a
          className="text-gray-500 dark:text-secondary inline-flex items-center whitespace-nowrap px-2 py-1 text-sm font-medium hover:brightness-150 cursor-pointer border border-zinc-800 rounded-md"
          onClick={() => {
            setOpen(true);
          }}
        >
          {orgContext.isLoading ? (
            <Spinner className="h-3 w-3 mr-1" />
          ) : (
            <span className="mr-1">
              {orgContext.activeOrg?.name
                ? orgContext.activeOrg.name.length > 20
                  ? orgContext.activeOrg.name.slice(0, 20) + "..."
                  : orgContext.activeOrg.name
                : ""}
            </span>
          )}
          <BuildingOfficeIcon className="w-5 h-5" />
        </a>
      </div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setOpen}>
          <div className="fixed inset-0" />

          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white dark:bg-zinc-900 py-6 shadow-xl">
                      <div className="px-4 sm:px-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                            Your Organization
                          </Dialog.Title>

                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="rounded-md bg-white dark:bg-zinc-900 text-gray-400 dark:text-secondary hover:text-gray-500 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-highlight focus:ring-offset-2 dark:focus:ring-offset-0"
                              onClick={() => setOpen(false)}
                            >
                              <span className="sr-only">Close panel</span>
                              <XCircleIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-row justify-between mt-2 mb-2">
                          <div className="flex flex-col items-end justify-end">
                            <p className="text-sm text-gray-700 dark:text-secondary">
                              Manage your organization here
                            </p>
                          </div>
                          <div className="flex flex-row h-[30px]">
                            <Button
                              onClick={() => {
                                setShowModal(true);
                              }}
                            >
                              <PlusIcon className="h-5 w-5" />
                              <span className="text-xs">
                                Create a new organization
                              </span>
                            </Button>
                          </div>
                          <AddOrgModal
                            isOpen={showModal}
                            setOpen={setShowModal}
                            onSuccess={() => {
                              oneClickDeployContext.reset();
                              setShowModal(false);
                            }}
                            onCancel={() => {
                              setShowModal(false);
                            }}
                          />
                        </div>
                        <OrgDropdown />
                        <OrgPageSettings />
                        <div className="w-full mt-4">
                          <Link
                            to="/org/settings"
                            className="text-sm text-highlight flex flex-row gap-2 items-center"
                            onClick={() => {
                              setOpen(false);
                            }}
                          >
                            <Cog6ToothIcon className="w-4 h-4" /> Organization
                            Settings
                          </Link>
                        </div>
                      </div>
                      <div className="relative mt-6 flex-1 px-4 sm:px-6">
                        {/* Your content */}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default OrgSettingsSlideOver;
